import {buildLoginUrlWithReturn} from "@/lib/utils/authUtils.ts";
import {ANALYZER_JWT_URL, ANALYZER_TOKEN_REUSE_MARGIN_SECONDS} from "@/pages/analyzer/Analyzer.constants.ts";
import {AnalyzerAuthError} from "@/pages/analyzer/Analyzer.types.ts";

let cachedToken: {token: string; exp: number} | null = null;

const getTokenExpiry = (token: string): number => {
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))) as {exp?: number};
    return payload.exp ?? 0;
  } catch {
    return 0;
  }
};

const getCachedToken = (): string | null =>
  cachedToken && Date.now() / 1000 < cachedToken.exp - ANALYZER_TOKEN_REUSE_MARGIN_SECONDS ? cachedToken.token : null;

const getToken = async (signal?: AbortSignal): Promise<{token: string; fromCache: boolean}> => {
  const cached = getCachedToken();
  if (cached) return {token: cached, fromCache: true};
  const response = await fetch(ANALYZER_JWT_URL, {credentials: "include", signal});
  if (response.status === 401) {
    const loginUrl = buildLoginUrlWithReturn(true);
    window.location.replace(loginUrl);
    throw new AnalyzerAuthError("Your session has expired. Redirecting to sign in…");
  }
  if (!response.ok) {
    throw new AnalyzerAuthError(
      response.status === 403 ? "You do not have access to AI Analysis." : `Authorization failed: ${response.status}`
    );
  }
  const {token} = (await response.json()) as {token: string};
  const tokenExpiry = getTokenExpiry(token);
  cachedToken = {token, exp: tokenExpiry};
  return {token, fromCache: false};
};

const fetchWithToken = (url: string, init: RequestInit, token: string): Promise<Response> => {
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  return fetch(url, {...init, headers});
};

export const analyzerFetch = async (url: string, init: RequestInit = {}): Promise<Response> => {
  const signal = init.signal ?? undefined;
  const {token, fromCache} = await getToken(signal);
  const response = await fetchWithToken(url, init, token);
  if (response.status !== 401 || !fromCache) return response;
  cachedToken = null;
  const {token: freshToken} = await getToken(signal);
  return fetchWithToken(url, init, freshToken);
};
